import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Input,
  Select,
  Button,
  Spin,
  Alert,
  Typography,
  Space,
  Empty,
  Badge,
  message,
  Grid
} from 'antd';
import {
  SearchOutlined,
  AppstoreOutlined,
  BarsOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ProductService, Product, ProductCategory } from '../services/productService';
import { useCartStore } from '../stores/cartStore';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

type ViewMode = 'grid' | 'list';

// Where the shopper had scrolled to, per search/category, so that coming back
// from a product detail page returns them to the product they clicked. This
// replaces the page number the list used to keep in the URL (issue #128):
// with every product on one page there is no page to come back to, only a
// position. sessionStorage, not component state, because the list unmounts.
const SCROLL_MEMORY_PREFIX = 'products:scroll:';

const scrollMemoryKey = (search: string, category: string) =>
  `${SCROLL_MEMORY_PREFIX}${JSON.stringify([search, category])}`;

const Products: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart, loading: cartLoading } = useCartStore();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const screens = Grid.useBreakpoint();

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');

  // Debouncing reference
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // The search/category the loaded products belong to, so a genuine change can
  // be told apart from a remount with the same query.
  const lastQueryKeyRef = useRef<string | null>(null);
  const hasLoadedRef = useRef(false);

  // Updates only the params it is given and leaves the rest of the query
  // string alone.
  const updateSearchParams = useCallback(
    (
      params: { search?: string; category?: string },
      options?: { replace?: boolean }
    ) => {
      setSearchParams((previous) => {
        const newParams = new URLSearchParams(previous);
        const apply = (key: string, value: string) => {
          if (value) {
            newParams.set(key, value);
          } else {
            newParams.delete(key);
          }
        };

        if (params.search !== undefined) apply('search', params.search);
        if (params.category !== undefined) apply('category', params.category);
        // The list no longer paginates, so a `page` from an older link is dead
        // weight in the URL (issue #130).
        newParams.delete('page');

        return newParams;
      }, { replace: options?.replace });
    },
    [setSearchParams]
  );

  // Debounce search term
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce delay
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Load categories on mount (with caching and loading guard)
  useEffect(() => {
    const loadCategories = async () => {
      if (categoriesLoading || categories.length > 0) return;
      
      setCategoriesLoading(true);
      try {
        const response = await ProductService.getCategories();
        if (response.success) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, []); // Empty dependency array is correct - only run once on mount

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      
      if (debouncedSearchTerm) {
        response = await ProductService.searchProducts(
          debouncedSearchTerm,
          ProductService.getCurrentPriceLevel()
        );
      } else if (selectedCategory) {
        response = await ProductService.getProductsByCategory(
          selectedCategory,
          ProductService.getCurrentPriceLevel()
        );
      } else {
        response = await ProductService.getAllProducts({
          priceLevel: ProductService.getCurrentPriceLevel()
        });
      }

      if (response.success) {
        setProducts(response.data);
      } else {
        setError('Failed to load products. Please try again.');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      const errorMessage = error instanceof Error && error.message.includes('429') 
        ? 'Too many requests. Please wait a moment and try again.'
        : 'Failed to load products. Please try again.';
      setError(errorMessage);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [debouncedSearchTerm, selectedCategory]);

  // Load products when search params change
  useEffect(() => {
    loadProducts();
  }, [loadProducts]); // loadProducts is memoized with useCallback, so this is safe

  // A link from before the list showed everything can still carry ?page=3.
  // There are no pages any more, so drop it rather than leave a param in the
  // URL that does nothing (issue #130).
  useEffect(() => {
    if (searchParams.has('page')) {
      updateSearchParams({}, { replace: true });
    }
  }, [searchParams, updateSearchParams]);

  // Put the shopper back where they were once the products they were looking
  // at are on screen. Only after loading: scrolling before the cards render
  // would land on an empty page. App.tsx scrolls every route change to the
  // top, so this has to run after that.
  useEffect(() => {
    const queryKey = JSON.stringify([debouncedSearchTerm, selectedCategory]);
    const queryChanged = lastQueryKeyRef.current !== null && lastQueryKeyRef.current !== queryKey;
    lastQueryKeyRef.current = queryKey;

    // A new search or category starts at the top of its own results.
    if (queryChanged) {
      sessionStorage.removeItem(scrollMemoryKey(debouncedSearchTerm, selectedCategory));
      return;
    }
    if (!hasLoadedRef.current || loading || products.length === 0) return;

    const key = scrollMemoryKey(debouncedSearchTerm, selectedCategory);
    const saved = Number(sessionStorage.getItem(key));
    if (!saved) return;
    sessionStorage.removeItem(key);

    const frame = requestAnimationFrame(() => window.scrollTo(0, saved));
    return () => cancelAnimationFrame(frame);
  }, [debouncedSearchTerm, selectedCategory, loading, products.length]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    updateSearchParams({ search: value, category: '' });
    setSelectedCategory('');
  };

  const handleCategoryChange = (category?: string) => {
    const newCategory = category || '';
    setSelectedCategory(newCategory);
    updateSearchParams({ category: newCategory, search: '' });
    setSearchTerm('');
  };

  const handleProductClick = (product: Product) => {
    // Remember how far down the list this product was, so coming back does not
    // mean scrolling past everything above it again.
    sessionStorage.setItem(
      scrollMemoryKey(debouncedSearchTerm, selectedCategory),
      String(window.scrollY)
    );
    // Carry the list's query string across so the detail page can send the
    // shopper back to the same search/category they came from.
    navigate(`/products/${product.SupersedeNo}/${product.UOM}`, {
      state: { listSearch: location.search }
    });
  };

  // The product card is far wider relative to its fixed image height on a phone
  // than on a desktop, so a photo framed for desktop loses its top and bottom to
  // the crop. Where the admin has supplied a mobile-specific photo (issue #126),
  // small screens use that instead.
  const cardImageUrl = (product: Product) =>
    ProductService.getProductImageUrl(
      (!screens.md && product.MobilePictureFull) || product.MainPictureFull
    );

  const handleAddToCart = async (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await addToCart(product.SupersedeNo, product.UOM, 1);
      message.success(`Added ${product.Description} to cart`);
    } catch (error) {
      console.error('Error adding to cart:', error);
      message.error('Failed to add item to cart');
    }
  };

  const renderGridView = () => {
    return (
      <Row gutter={[16, 16]}>
        {products.map((product) => (
          <Col xs={24} sm={12} md={8} lg={6} key={`${product.SupersedeNo}-${product.UOM}`}>
            <Card
              hoverable
              className="dasoon-card product-card"
              cover={
                <div className="product-image-container">
                  <img
                    alt={product.Description}
                    src={cardImageUrl(product)}
                    // The whole catalogue is on one page now, so photos below
                    // the fold wait until they are scrolled to (issue #130).
                    loading="lazy"
                    style={{ width: '100%', height: 200, objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/product-placeholder.svg';
                    }}
                  />
                </div>
              }
              actions={[
                <Button
                  type="primary"
                  className="dasoon-btn-primary"
                  icon={<ShoppingCartOutlined />}
                  onClick={(e) => handleAddToCart(product, e)}
                  loading={cartLoading}
                  style={{ width: '100%' }}
                >
                  Add to Cart
                </Button>
              ]}
              onClick={() => handleProductClick(product)}
            >
              <Card.Meta
                title={
                  <Text ellipsis={{ tooltip: product.Description }}>
                    {product.Description}
                  </Text>
                }
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">{product.UOM}</Text>
                    <div>
                      <Text strong style={{
                        color: 'var(--dasoon-primary)',
                        fontFamily: 'var(--font-headers)'
                      }}>
                        {ProductService.priceWithoutGstLabel(product.PriceDesc1)}
                      </Text>
                    </div>
                  </Space>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderListView = () => {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {products.map((product) => (
          <Card
            key={`${product.SupersedeNo}-${product.UOM}`}
            hoverable
            className="dasoon-card product-list-card"
            onClick={() => handleProductClick(product)}
          >
            <Row gutter={16} align="middle">
              <Col xs={24} sm={6} md={4}>
                <img
                  alt={product.Description}
                  src={cardImageUrl(product)}
                  loading="lazy"
                  style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/product-placeholder.svg';
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={14}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {product.Description}
                  </Title>
                  <Text type="secondary">UOM: {product.UOM}</Text>
                  {product.prodDetail && (
                    <Text ellipsis={{ tooltip: product.prodDetail }}>
                      {product.prodDetail}
                    </Text>
                  )}
                </Space>
              </Col>
              <Col xs={24} sm={6} md={6} className="text-right">
                <Space direction="vertical" align="end" size="small">
                  <div>
                    <Text strong style={{
                      color: 'var(--dasoon-primary)',
                      fontSize: '1.1em',
                      fontFamily: 'var(--font-headers)'
                    }}>
                      {ProductService.priceWithoutGstLabel(product.PriceDesc1)}
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    className="dasoon-btn-primary"
                    icon={<ShoppingCartOutlined />}
                    onClick={(e) => handleAddToCart(product, e)}
                    loading={cartLoading}
                  >
                    Add to Cart
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>
    );
  };

  return (
    <div className="products-page" style={{ padding: 'var(--spacing-3xl) var(--spacing-lg) var(--spacing-lg)' }}>
      <div className="container">
        {/* Page Header */}
        <div style={{ marginBottom: 'var(--spacing-2xl)' }}>
          <Title level={2} style={{ 
            color: 'var(--dasoon-black)', 
            marginBottom: 'var(--spacing-md)',
            fontFamily: 'var(--font-headers)',
            letterSpacing: 'var(--font-headers-spacing)'
          }}>
            Our Products
          </Title>
          {searchTerm && (
            <Text type="secondary">
              Search results for: <strong>"{searchTerm}"</strong> ({products.length} products found)
            </Text>
          )}
          {selectedCategory && (
            <Text type="secondary">
              Category: <strong>{selectedCategory}</strong> ({products.length} products found)
            </Text>
          )}
        </div>

        {/* Search and Filter Bar */}
        <Card className="dasoon-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="Search products..."
                allowClear
                enterButton={<SearchOutlined />}
                onSearch={handleSearch}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="All Categories"
                allowClear
                style={{ width: '100%' }}
                value={selectedCategory || undefined}
                onChange={handleCategoryChange}
              >
                {categories.map((category) => (
                  <Option key={category.Category} value={category.Category}>
                    {category.CategoryDescription || category.Category}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} sm={12} md={4}>
              <Button.Group>
                <Button
                  type={viewMode === 'grid' ? 'primary' : 'default'}
                  className={viewMode === 'grid' ? 'dasoon-btn-primary' : ''}
                  icon={<AppstoreOutlined />}
                  onClick={() => setViewMode('grid')}
                />
                <Button
                  type={viewMode === 'list' ? 'primary' : 'default'}
                  className={viewMode === 'list' ? 'dasoon-btn-primary' : ''}
                  icon={<BarsOutlined />}
                  onClick={() => setViewMode('list')}
                />
              </Button.Group>
            </Col>
            <Col xs={12} sm={12} md={6} className="text-right">
              <Text type="secondary">
                {products.length} products found
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Content */}
        {loading ? (
          <div className="text-center" style={{ padding: 'var(--spacing-3xl)' }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={loadProducts}>
                Retry
              </Button>
            }
          />
        ) : products.length === 0 ? (
          <Empty
            description={
              searchTerm || selectedCategory
                ? 'No products found matching your criteria'
                : 'No products available'
            }
          />
        ) : (
          // Every product on one page: no pagination for the shopper to click
          // through (issue #130).
          viewMode === 'grid' ? renderGridView() : renderListView()
        )}
      </div>
    </div>
  );
};

export default Products;